/**
 * Cross-feature canvas-focus channel — the IR plan's "trace map" gesture
 * (UX/UI report C3) and the schematic↔PCB cross-probe channel.
 *
 * Any surface that mentions a component (BOM rows, DRC findings, future
 * net-table rows, future "click MPN in chat") writes the component
 * **refdes** into this store on hover / focus / select. Any surface
 * that renders a CircuitCanvas reads from it and threads the value
 * into its highlight model.
 *
 * **Why refdes, not id?** The BOM tab's GraphComponent rows only carry
 * the refdes — the UUID never makes it into the projection. Refdes is
 * unique within a single circuit, so it's a sound key for cross-surface
 * focus signalling. The canvas resolves refdes → id internally (cheap
 * — IR is already indexed in the canvas).
 *
 * Channels:
 *
 *   * `hoveredRefdes` — transient component hover, set on pointer-over,
 *     cleared on pointer-out. Drives a soft halo.
 *
 *   * `hoveredNetId` — transient net hover, set when the cursor enters
 *     a track / via / zone on a given net. PCB and schematic both
 *     subscribe so hovering a net in one tab lights the equivalent
 *     symbol pins / nets in the other (the "cross-probe net" gesture
 *     every CAD engineer expects).
 *
 *   * `selectedSet` — sticky multi-selection. Click without a modifier
 *     replaces the set; Shift+click adds; ⌘/Ctrl+click toggles. Esc
 *     clears. Drives strong haloes and (in future) batch operations.
 *
 *   * `selectedRefdes` — derived "primary" selection (the most recent
 *     addition to the set). Retained as a discrete field for
 *     backwards-compat with consumers that only care about a single
 *     focused component — BOM detail panel, the agent's "Ask about R5"
 *     prefill, the URL `?sel=` param. New consumers should prefer
 *     `selectedSet` membership checks.
 *
 * Decoupled from `draftStore` because this channel is shared with
 * read-only surfaces (project detail, committed versions) that have
 * no relationship to the agent's working buffer.
 */
import { useMemo } from "react";
import { create } from "zustand";

interface CanvasFocusState {
  hoveredRefdes: string | null;
  hoveredNetId: string | null;
  selectedSet: ReadonlySet<string>;
  /** Primary selection — most-recently-added refdes in `selectedSet`,
   *  or null when the set is empty. Retained for backwards compat. */
  selectedRefdes: string | null;
}

interface CanvasFocusActions {
  setHovered: (refdes: string | null) => void;
  setHoveredNet: (netId: string | null) => void;
  /** Replace the selection with a single refdes (or clear when null). */
  setSelected: (refdes: string | null) => void;
  /** Add a refdes to the set without disturbing existing members. */
  addToSelection: (refdes: string) => void;
  /** Toggle a refdes: add if absent, remove if present. */
  toggleSelection: (refdes: string) => void;
  /** Replace the set wholesale — used by marquee select + BOM
   *  "select all rows" gestures. */
  setSelectionSet: (refdes: readonly string[]) => void;
  /** Clear the selection entirely. */
  clearSelection: () => void;
  /** Convenience — clear EVERYTHING (hover + selection). */
  clearAll: () => void;
}

export const useCanvasFocusStore = create<
  CanvasFocusState & CanvasFocusActions
>()((set) => ({
  hoveredRefdes: null,
  hoveredNetId: null,
  selectedSet: new Set<string>(),
  selectedRefdes: null,
  setHovered: (refdes) => set({ hoveredRefdes: refdes }),
  setHoveredNet: (netId) => set({ hoveredNetId: netId }),
  setSelected: (refdes) =>
    set({
      selectedSet: refdes ? new Set([refdes]) : new Set(),
      selectedRefdes: refdes,
    }),
  addToSelection: (refdes) =>
    set((state) => {
      const next = new Set(state.selectedSet);
      next.add(refdes);
      return { selectedSet: next, selectedRefdes: refdes };
    }),
  toggleSelection: (refdes) =>
    set((state) => {
      const next = new Set(state.selectedSet);
      let primary: string | null;
      if (next.has(refdes)) {
        next.delete(refdes);
        // After removing, fall back to any remaining member as the
        // new primary. Set iteration order is insertion order, so
        // picking the last yields "the most-recent surviving pick".
        const survivors = [...next];
        primary = survivors.length > 0 ? survivors[survivors.length - 1] : null;
      } else {
        next.add(refdes);
        primary = refdes;
      }
      return { selectedSet: next, selectedRefdes: primary };
    }),
  setSelectionSet: (refdes) =>
    set({
      selectedSet: new Set(refdes),
      selectedRefdes: refdes.length > 0 ? refdes[refdes.length - 1] : null,
    }),
  clearSelection: () =>
    set({ selectedSet: new Set(), selectedRefdes: null }),
  clearAll: () =>
    set({
      hoveredRefdes: null,
      hoveredNetId: null,
      selectedSet: new Set(),
      selectedRefdes: null,
    }),
}));

/**
 * Convenience selector — returns the union of hover + select refdes as
 * a Set, ready to hand to `CircuitCanvas`'s `highlightRefdes` prop.
 *
 * Memoised on the underlying ids so a downstream `React.memo` consumer
 * (CircuitCanvas, the BOM-row halo) sees a stable Set reference between
 * renders that didn't actually change the highlight set. Without this,
 * any unrelated parent rerender constructs a new Set and forces every
 * canvas/halo subtree to recompute.
 */
export function useCanvasHighlightRefdes(): ReadonlySet<string> {
  const hovered = useCanvasFocusStore((s) => s.hoveredRefdes);
  const selectedSet = useCanvasFocusStore((s) => s.selectedSet);
  return useMemo(() => {
    if (!hovered) return selectedSet;
    if (selectedSet.has(hovered)) return selectedSet;
    const merged = new Set(selectedSet);
    merged.add(hovered);
    return merged;
  }, [hovered, selectedSet]);
}
